'use client';

import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';

interface Ticket {
  id: string;
  subject: string;
  category: string;
  priority: string;
  message: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  admin_reply: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  open: { label: 'Open', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  in_progress: { label: 'In Progress', color: '#818cf8', bg: 'rgba(99,102,241,0.12)' },
  resolved: { label: 'Resolved', color: '#10b981', bg: 'rgba(16,185,129,0.12)' },
  closed: { label: 'Closed', color: '#6b7280', bg: 'rgba(107,114,128,0.12)' },
};

const CATEGORIES = ['General', 'Distribution', 'Royalties', 'Technical', 'Account', 'Billing'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

export default function SupportPage() {
  const [tab, setTab] = useState<'create' | 'tickets'>('create');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [form, setForm] = useState({ subject: '', category: 'General', priority: 'medium', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  async function fetchTickets() {
    setLoadingTickets(true);
    try {
      const res = await fetch('/api/tickets');
      if (!res.ok) return;
      const json = await res.json();
      setTickets(json.tickets || []);
    } catch (e) {
      console.error('Failed to load tickets:', e);
    } finally {
      setLoadingTickets(false);
    }
  }

  useEffect(() => {
    if (tab === 'tickets') fetchTickets();
  }, [tab]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setSubmitMsg(null);
    try {
      const res = await fetch('/api/tickets/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const json = await res.json();
      if (!res.ok) { setSubmitMsg({ type: 'error', text: json.error || 'Submission failed' }); return; }
      setSubmitMsg({ type: 'success', text: 'Ticket submitted successfully! Our team will respond within 24 hours.' });
      setForm({ subject: '', category: 'General', priority: 'medium', message: '' });
    } catch (e: any) {
      setSubmitMsg({ type: 'error', text: e.message });
    } finally {
      setSubmitting(false);
      setTimeout(() => setSubmitMsg(null), 5000);
    }
  }

  const inputClass = "w-full px-4 py-2.5 rounded-xl text-sm outline-none";
  const inputStyle = { background: 'var(--card-elevated)', border: '1px solid var(--border)', color: 'var(--foreground)' };

  return (
    <AppLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--foreground)' }}>Support Center</h1>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Get help with your releases, royalties, and account</p>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 p-1 rounded-xl w-fit" style={{ background: 'var(--muted)' }}>
          {[{ id: 'create', label: 'Submit Ticket' }, { id: 'tickets', label: 'My Tickets' }].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id as any)} className="px-5 py-2 rounded-lg text-sm font-semibold transition-all" style={{ background: tab === t.id ? 'var(--primary)' : 'transparent', color: tab === t.id ? 'white' : 'var(--muted-foreground)' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Submit Ticket Tab */}
        {tab === 'create' && (
          <div className="rounded-2xl p-6 max-w-2xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <h3 className="text-base font-bold mb-5" style={{ color: 'var(--foreground)' }}>Submit a Support Ticket</h3>

            {submitMsg && (
              <div className="flex items-center gap-3 p-4 rounded-xl mb-5" style={{ background: submitMsg.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${submitMsg.type === 'success' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
                <Icon name={submitMsg.type === 'success' ? 'CheckCircleIcon' : 'ExclamationCircleIcon'} size={18} style={{ color: submitMsg.type === 'success' ? '#10b981' : '#ef4444' }} />
                <p className="text-sm font-semibold" style={{ color: submitMsg.type === 'success' ? '#10b981' : '#ef4444' }}>{submitMsg.text}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>Subject *</label>
                <input value={form.subject} onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))} placeholder="Brief description of your issue" className={inputClass} style={inputStyle} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>Category</label>
                  <select value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} className={inputClass} style={inputStyle}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>Priority</label>
                  <select value={form.priority} onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))} className={inputClass} style={inputStyle}>
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>Message *</label>
                <textarea value={form.message} onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))} rows={5} placeholder="Describe your issue in detail..." className={`${inputClass} resize-none`} style={inputStyle} required />
              </div>
              <div className="flex justify-end">
                <button type="submit" disabled={submitting} className="px-6 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50" style={{ background: 'var(--primary)' }}>
                  {submitting ? 'Submitting...' : 'Submit Ticket'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* My Tickets Tab */}
        {tab === 'tickets' && (
          <div className="rounded-2xl p-6" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <h3 className="text-base font-bold mb-5" style={{ color: 'var(--foreground)' }}>My Tickets</h3>
            {loadingTickets ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="LifebuoyIcon" size={36} className="mx-auto mb-3" style={{ color: 'var(--muted-foreground)' }} />
                <p className="text-sm font-semibold mb-1" style={{ color: 'var(--foreground)' }}>No tickets yet</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Submit a ticket if you need help.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tickets.map((ticket) => {
                  const sc = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
                  return (
                    <div key={ticket.id} className="p-4 rounded-xl" style={{ background: 'var(--card-elevated)', border: '1px solid var(--border)' }}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate" style={{ color: 'var(--foreground)' }}>{ticket.subject}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{ticket.category}</span>
                            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>·</span>
                            <span className="text-xs capitalize" style={{ color: 'var(--muted-foreground)' }}>{ticket.priority} priority</span>
                            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>·</span>
                            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{new Date(ticket.created_at).toLocaleDateString('en-IN')}</span>
                          </div>
                          <p className="text-xs mt-2 line-clamp-2" style={{ color: 'var(--muted-foreground)' }}>{ticket.message}</p>
                          {ticket.admin_reply && (
                            <div className="mt-3 p-3 rounded-lg" style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)' }}>
                              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--primary-light)' }}>Support Reply:</p>
                              <p className="text-xs" style={{ color: 'var(--foreground)' }}>{ticket.admin_reply}</p>
                            </div>
                          )}
                        </div>
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0" style={{ background: sc.bg, color: sc.color }}>{sc.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
